import isEqual from 'lodash-es/isEqual';
import * as moment from 'moment';

import { AddressValidationError } from '~/components/AddressValidation/utils';
import { type Option } from '~/components/Select';
import { ADDRESS_INPUT_FIELDS } from '~/constants/addressBook';
import {
  Country,
  countries,
  countriesWithoutZip,
  uspsTrackingCountryAdditionalWhitelist,
  uspsTrackingCountryIso2Whitelist,
  type CountryIso2,
} from '~/constants/countries';
import states, { usMilitaryCities, usMilitaryStates, usTerritoryStates } from '~/constants/states';
import { type OrderV2ShipmentAddress } from '~/modules/orders/types/ordersV2.type';
import { type OriginalAddress } from '~/services/address-validation.service';
import { StoreName } from '~/types/Store';
import { formatCountryToOption } from '~/utils/field-formaters';

import type APIAddress from './APIAddress';
import type Order from './Order';

import { type BatchEntryExtended } from './OrderBatchEntry';

export { Country };
export type AddressToType = 'to';
export type AddressSenderType = 'sender';
export type AddressReturnType = 'return';
export type AddressType = AddressReturnType | AddressSenderType | AddressToType;

export type State = {
  iso: string;
  name: string;
};

export interface JSONBaseAddress {
  city: string;
  company: string;
  country: string;
  email: string;
  name: string;
  phone: string;
  state: string;
  street_no: Maybe<string>;
  street1: string;
  street2: Maybe<string>;
  zip: string;
}

export enum AddressTypeV2 {
  COMMERCIAL = 'commercial',
  RESIDENTIAL = 'residential',
  UNKNOWN = 'unknown',
}

export interface JSONAddressV2 {
  address_line_1: string;
  address_line_2: Maybe<string>;
  city_locality: string;
  country_code: CountryIso2;
  postal_code: string;
  state_province: string;
}

export interface JSONAddressSearchV2 {
  address: string;
  id: string;
  type: 'address' | 'container';
}

export interface JSONAddressSearchV2Response {
  count: number;
  limit: number;
  offset: number;
  results: JSONAddressSearchV2[];
}

export interface JSONAddressForAddressBook extends JSONAddressV2 {
  address_type: AddressTypeV2;
  email: string;
  name: string;
  organization: string;
  phone: string;
  street_no: Maybe<string>;
}

export interface JSONAddressBookAddress {
  address: JSONAddressForAddressBook;
  created_at: string;
  id: string;
  updated_at: string;
}

type JSONAddressValidationResults = {
  is_valid: boolean;
  messages?: { code: string; text: string }[];
};

export interface JSONAddress extends JSONBaseAddress {
  city: string;
  company: string;
  country: string;
  email: string;
  id: number;
  ip: Maybe<string>;
  is_confirmed_by_user?: boolean;
  is_residential: boolean | null;
  latitude?: Maybe<string>;
  longitude?: Maybe<string>;
  messages: string[];
  metadata: string;
  name: string;
  object_created: string;
  object_id: string;
  object_owner: string;
  object_purpose: string;
  object_source: string;
  object_state: string;
  object_updated: string;
  phone: string;
  state: string;
  street_no: Maybe<string>;
  street1: string;
  street2: Maybe<string>;
  street3: string;
  test: boolean;
  validation_results?: JSONAddressValidationResults;
  zip: string;
}

export interface DisplayInvoiceAddress {
  city: string;
  company: string;
  country: string;
  email: string;
  state?: string;
  street1: string;
  street2?: string;
  zip: string;
}

type AddressValidationResults = {
  isValid: boolean;
  messages: { code: AddressValidationError; text: string }[];
};

export interface BaseAddress {
  city: string;
  company: string;
  country: Maybe<Country>;
  email: string;
  isConfirmedByUser?: boolean;
  isResidential?: boolean | null;
  name: string;
  objectId?: string;
  phone: Maybe<string>;
  state: string;
  street1: string;
  street2: Maybe<string>;
  streetNumber: Maybe<string>;
  validationResults?: AddressValidationResults;
  zip: string;
}

type ValidatedAddress = Omit<BaseAddress, 'email' | 'phone'>;
export type AddressInfo = Pick<
  BaseAddress,
  'city' | 'country' | 'state' | 'street1' | 'street2' | 'streetNumber' | 'zip'
>;

interface Address extends BaseAddress {
  id: number;
  ip: Maybe<string>;
  isResidential: boolean | null;
  latitude?: Maybe<string>;
  longitude?: Maybe<string>;
  messages: string[];
  metadata: string;
  objectCreated: Date;
  objectId: string;
  objectOwner: string;
  objectPurpose: string;
  objectSource: string;
  objectState: string;
  objectUpdated: Date;
  street3: string;
  streetNumber: Maybe<string>;
  test: boolean;
}

export interface AddressIt {
  country: string;
  number: string;
  parts: string[];
  postalcode: string;
  regions: string[];
  state: string;
  street: string;
  text: string;
  unit: string;
}

export interface PastedAddress {
  BuildingName?: string;
  BuildingNumber: string;
  City: string;
  CountryIso2: CountryIso2;
  CountryName: string;
  Line1?: string;
  Line2?: string;
  PostalCode: string;
  ProvinceCode: string;
  ProvinceName: string;
  Street: string;
  SubBuilding: string;
}

export type WebAddress = {
  city: string;
  company: string;
  country: CountryIso2;
  email: string;
  ip: string;
  is_residential: boolean;
  latitude: string;
  longitude: string;
  metadata: string;
  name: string;
  object_created: Date;
  object_id: string;
  object_purpose: 'PURCHASE' | 'QUOTE';
  object_source: string;
  object_state: string;
  object_updated: Date;
  phone: string;
  state: string;
  street_no: string;
  street1: string;
  street2: string;
  street3: string;
  zip: string;
};

export const fromJSON = (jsonAddress: JSONAddress): Maybe<Address> => {
  if (!jsonAddress) return null;

  return {
    city: jsonAddress.city,
    company: jsonAddress.company,
    country: countries[jsonAddress.country as keyof typeof countries],
    email: jsonAddress.email,
    id: jsonAddress.id,
    ip: jsonAddress.ip,
    isConfirmedByUser: jsonAddress.is_confirmed_by_user || false,
    isResidential: jsonAddress.is_residential,
    latitude: jsonAddress.latitude,
    longitude: jsonAddress.longitude,
    messages: jsonAddress.messages,
    metadata: jsonAddress.metadata,
    name: jsonAddress.name,
    objectCreated: new Date(jsonAddress.object_created),
    objectId: jsonAddress.object_id,
    objectOwner: jsonAddress.object_owner,
    objectPurpose: jsonAddress.object_purpose,
    objectSource: jsonAddress.object_source,
    objectState: jsonAddress.object_state,
    objectUpdated: new Date(jsonAddress.object_updated),
    phone: jsonAddress.phone,
    state: jsonAddress.state,
    street1: jsonAddress.street1,
    street2: jsonAddress.street2,
    street3: jsonAddress.street3,
    streetNumber: jsonAddress.street_no,
    test: jsonAddress.test,
    validationResults: jsonAddress.validation_results
      ? {
          isValid: jsonAddress.validation_results.is_valid,
          messages: (jsonAddress.validation_results.messages || [])
            .filter(({ code }) => !!AddressValidationError[code.toUpperCase()])
            .map(({ code, text }) => ({
              code: code.toUpperCase() as AddressValidationError,
              text,
            })),
        }
      : undefined,
    zip: jsonAddress.zip,
  };
};

/**
 * This is a mapper function to map JSONAddressBookAddress to BaseAddress
 * This is used to parse the Address book search API response and feed it into
 * the existing address forms
 * @param jsonAddress
 * @returns
 */
export const fromAddressBookJSON = (jsonAddress: JSONAddressBookAddress): Maybe<BaseAddress> => {
  if (!jsonAddress) return null;
  return {
    city: jsonAddress.address.city_locality,
    company: jsonAddress.address.organization,
    country: countries[jsonAddress.address.country_code as keyof typeof countries],
    email: jsonAddress.address.email,
    isResidential: jsonAddress.address.address_type === AddressTypeV2.RESIDENTIAL,
    name: jsonAddress.address.name,
    objectId: jsonAddress.id,
    phone: jsonAddress.address.phone,
    state: jsonAddress.address.state_province,
    street1: jsonAddress.address.address_line_1,
    street2: jsonAddress.address.address_line_2,
    streetNumber: jsonAddress.address.street_no,
    zip: jsonAddress.address.postal_code,
  };
};

export const fromAddressValidationJSON = (jsonAddress: OriginalAddress): ValidatedAddress => ({
  city: jsonAddress.city_locality,
  company: jsonAddress.organization,
  country: countries[jsonAddress.country_code as keyof typeof countries],
  name: jsonAddress.name,
  state: jsonAddress.state_province,
  street1: jsonAddress.address_line_1,
  street2: jsonAddress.address_line_2,
  streetNumber: '',
  zip: jsonAddress.postal_code,
});

/**
 * This is to map the BaseAddress to JSONAddressBookAddress
 * @param address
 * @returns
 */
export const toAddressBookJSON = (address: BaseAddress): JSONAddressBookAddress => ({
  address: {
    address_line_1: address.street1,
    address_line_2: address.street2,
    // If it's not residential then it's unknown
    address_type: address.isResidential ? AddressTypeV2.RESIDENTIAL : AddressTypeV2.UNKNOWN,
    city_locality: address.city,
    country_code: address.country?.iso2 || countries.US.iso2,
    email: address.email,
    name: address.name,
    organization: address.company,
    phone: address.phone || '',
    postal_code: address.zip,
    state_province: address.state,
    street_no: address.streetNumber,
  },
  created_at: '',
  // This is okay to be empty because we don't use these to update the value
  id: address.objectId!,
  // These values are updated by the backend irrespective of what we send
  updated_at: '',
});

export const toJSON = (address: Address): JSONAddress => ({
  city: address.city,
  company: address.company,
  // TODO: I dunno if empty string is correct
  country: address.country ? address.country.iso2 : '',
  email: address.email,
  id: address.id,
  ip: address.ip,
  is_residential: address.isResidential,
  latitude: address.latitude,
  longitude: address.longitude,
  messages: address.messages,
  metadata: address.metadata,
  name: address.name,
  object_created: address.objectCreated?.toISOString(),
  object_id: address.objectId,
  object_owner: address.objectOwner,
  object_purpose: address.objectPurpose,
  object_source: address.objectSource,
  object_state: address.objectState,
  object_updated: address.objectUpdated?.toISOString(),
  phone: address.phone ? address.phone : '',
  state: address.state,
  street_no: address.streetNumber,
  street1: address.street1,
  street2: address.street2,
  street3: address.street3,
  test: address.test,
  zip: address.zip,
});

export const fromWebAddress = (webAddress: WebAddress): Address => {
  const country: Country | undefined = webAddress.country
    ? countries[webAddress.country.toUpperCase()]
    : undefined;
  return {
    city: webAddress.city,
    company: webAddress.company,
    country,
    email: webAddress.email,
    id: -1,
    ip: webAddress.ip,
    isResidential: webAddress.is_residential,
    latitude: webAddress.latitude,
    longitude: webAddress.longitude,
    messages: [],
    metadata: webAddress.metadata,
    name: webAddress.name,
    objectCreated: moment(webAddress.object_created).toDate(),
    objectId: webAddress.object_id,
    //nulls
    objectOwner: '',
    objectPurpose: webAddress.object_purpose,
    objectSource: webAddress.object_source,
    objectState: webAddress.object_state,
    objectUpdated: moment(webAddress.object_updated).toDate(),
    phone: webAddress.phone,
    state: webAddress.state,
    street1: webAddress.street1,
    street2: webAddress.street2,

    street3: webAddress.street3,
    streetNumber: webAddress.street_no,
    test: false,
    zip: webAddress.zip,
  };
};

export const countryUsesStreetNumberSuffix = (country: Maybe<Country>) => {
  if (!country) return false;

  return ['AT', 'CH', 'DE'].includes(country.iso2);
};

export const sanitizeAddress = (address: BaseAddress) =>
  !countryUsesStreetNumberSuffix(address.country)
    ? {
        ...address,
        streetNumber: null,
      }
    : address;

export const getStreetLine = (
  address: Pick<BaseAddress, 'country' | 'street1' | 'streetNumber'> | undefined,
) => {
  if (!address) return '';

  return countryUsesStreetNumberSuffix(address.country)
    ? `${address.streetNumber} ${address.street1}`
    : `${address.street1}`;
};

export const getRequiredAddressFieldsByCountry = (country: Maybe<Country>): Maybe<string[]> => {
  if (!country) return;

  const requireState = isStateMandatory(country);
  const requireStreetNumSuffix = countryUsesStreetNumberSuffix(country);

  if (requireState) return ADDRESS_INPUT_FIELDS.requireStateField;

  if (requireStreetNumSuffix) return ADDRESS_INPUT_FIELDS.requireStreetNumberSuffix;

  return ADDRESS_INPUT_FIELDS.minRequireFields;
};

export const isAddressFieldsComplete = (
  address: BaseAddress,
  requiredFields: Maybe<string[]>,
): boolean => {
  if (!Array.isArray(requiredFields)) return false;
  return requiredFields.every((field) => address[field as keyof BaseAddress]);
};

export const isStateMandatory = (country: Maybe<Country>): boolean =>
  isEqual(country, countries.US) ||
  isEqual(country, countries.CA) ||
  isEqual(country, countries.IE);

const isOrdersV2ShipmentAddress = (
  address: APIAddress | OrderV2ShipmentAddress,
): address is OrderV2ShipmentAddress => address.hasOwnProperty('country_iso2');

export const isAddressIncomplete = (
  address?: APIAddress | OrderV2ShipmentAddress | null,
): boolean => {
  if (!address) return isAddressMissingRequiredFields(address);

  if (isOrdersV2ShipmentAddress(address)) return isAddressMissingRequiredFields(address);

  return isAddressMissingRequiredFields({
    city: address.city,
    country_iso2: address.country ? address.country.iso2 : '',
    name: address.name,
    state: address.state,
    store_platform_names: address.storePlatformNames,
    street_no: address.streetNumber || '',
    street1: address.street1,
    zip: address.zip,
  });
};

const isAddressMissingRequiredFields = (
  address?:
    | ({
        store_platform_names?: APIAddress['storePlatformNames'];
      } & Pick<
        OrderV2ShipmentAddress,
        'city' | 'country_iso2' | 'name' | 'state' | 'street_no' | 'street1' | 'zip'
      >)
    | null,
): boolean => {
  if (!address) return true;

  const missingMinimumRequiredFields =
    !address.country_iso2 || !address.city || !address.street1 || !address.name;
  if (missingMinimumRequiredFields) return true;

  const isShopify =
    address.hasOwnProperty('store_platform_names') &&
    address.store_platform_names?.includes(StoreName.Shopify);

  const country = countries[address.country_iso2 as keyof typeof countries];
  const missingState = isStateMandatory(country) && !address.state;
  const missingStreetNumber =
    !isShopify && countryUsesStreetNumberSuffix(country) && !address.street_no;
  const missingZip = !address.zip && countryRequiresZip(country);

  return missingState || missingStreetNumber || missingZip;
};

export const isBaseAddress = (address: BaseAddress | number): address is BaseAddress =>
  (<BaseAddress>address).street1 !== undefined;

export const areAddressesEqual = (
  firstAddress: Maybe<BaseAddress>,
  secondAddress: Maybe<BaseAddress>,
): boolean => {
  if (!firstAddress || !secondAddress) return firstAddress === secondAddress;

  return (
    firstAddress.street1 === secondAddress.street1 &&
    firstAddress.street2 === secondAddress.street2 &&
    firstAddress.streetNumber === secondAddress.streetNumber &&
    firstAddress.zip === secondAddress.zip
  );
};

export const areAddressesFullyEqual = (
  firstAddress: Maybe<BaseAddress>,
  secondAddress: Maybe<BaseAddress>,
): boolean => {
  if (!firstAddress || !secondAddress) return firstAddress === secondAddress;

  if (!areAddressesEqual(firstAddress, secondAddress)) return false;

  return (
    firstAddress.name === secondAddress.name &&
    firstAddress.company === secondAddress.company &&
    firstAddress.phone === secondAddress.phone &&
    firstAddress.email === secondAddress.email &&
    firstAddress.city === secondAddress.city &&
    firstAddress.state === secondAddress.state &&
    firstAddress.country?.iso2 === secondAddress.country?.iso2
  );
};

export const areAllAddressesEqual = (addresses: BaseAddress[]): boolean => {
  if (addresses.length < 2) return false;
  for (let i = 0; i < addresses.length - 1; i += 1) {
    const j = i + 1;
    if (!areAddressesEqual(addresses[i], addresses[j])) return false;
  }
  return true;
};

export const areAllAddressesFullyEqual = (addresses: BaseAddress[]): boolean => {
  if (addresses.length < 2) return false;
  for (let i = 0; i < addresses.length - 1; i += 1)
    if (!areAddressesFullyEqual(addresses[i], addresses[i + 1])) return false;

  return true;
};

export const getCountryStates = (country: Country): State[] => {
  const countryStates = states[country.iso2];

  if (countryStates) return Object.values(countryStates);

  return [];
};

export const getStateByCountry = (state: string, country: Country): State => {
  const countryStates = states[country.iso2];

  if (countryStates) return countryStates[state];

  return {
    iso: state,
    name: state,
  };
};

export const countryUsesStateList = (country: Country): boolean =>
  country.iso2 === countries.US.iso2 ||
  country.iso2 === countries.CA.iso2 ||
  country.iso2 === countries.AU.iso2;

export const getStateOptions = (states: State[]): Option<string>[] =>
  states.map((state) => ({ label: state.name, value: state.iso }));

export const getCountryOptions = (
  suggestedCountryCode?: string,
  icon = false,
): Option<string>[] => {
  const options = Object.values(countries).map((c) => formatCountryToOption(c, icon)!);

  if (!suggestedCountryCode) return options;

  const defaultCountry: Country | undefined =
    countries[suggestedCountryCode as keyof typeof countries];
  if (!defaultCountry) return options;

  const defaultCountryAsOption = formatCountryToOption(defaultCountry, icon);

  return options.sort((a) => {
    if (a.value === defaultCountryAsOption.value) return -(options.length + 1);

    return 0;
  });
};

// By taking the country options as an input, we're able to avoid creating a new array on every key press
export const countryMatchingInput = (
  countryOptions: Option<string>[],
  inputCountryName: string,
): Country | undefined => {
  const match = countryOptions.find(
    (c) => (c.label as string).toLowerCase() === inputCountryName.toLowerCase(),
  );
  if (!match) return;

  return countries[match.value as CountryIso2];
};

export const inputValueMatchCountry = (v: string): Country | undefined =>
  Object.values(countries).find((c) => {
    if (c.name.toLowerCase() === v.toLowerCase() || c.iso2.toLowerCase() === v.toLowerCase())
      return c;

    return;
  });

export const inputValueMatchState = (
  v: string,
  countryStates: { [key: string]: State },
): State | undefined =>
  Object.values(countryStates).find((s) => {
    if (s.name.toLowerCase() === v.toLowerCase() || s.iso.toLowerCase() === v.toLowerCase())
      return s;

    return;
  });

export const countryRequiresZip = (country: Country): boolean =>
  !countriesWithoutZip.find((c) => c.iso2 === country.iso2);

export const shortUSZipCode = (zip: string): string => zip.substring(0, 5);

export const isMilitaryAddress = (address: Pick<BaseAddress, 'city' | 'state'>): boolean =>
  (!!address.state &&
    Object.values(usMilitaryStates)
      .map((state) => state.iso)
      .includes(address.state.toUpperCase())) ||
  (!!address.city && usMilitaryCities.includes(address.city.toUpperCase()));

export const isUSTerritory = (address: Pick<BaseAddress, 'state'>): boolean =>
  !!address.state &&
  Object.values(usTerritoryStates)
    .map((state) => state.iso)
    .includes(address.state.toUpperCase());

export const isUSorMilitaryTerritory = (territory: string) =>
  Object.values({ ...usTerritoryStates, ...usMilitaryStates })
    .map((state) => state.iso)
    .includes(territory.toUpperCase());

// TODO: Properly type this Order, Maybe<BatchEntry>, returned are Maybe<Address> so consumers need to be updated
export const getAddresses = (
  order: Order,
  batchEntry?: BatchEntryExtended | null,
): { addressFrom: Address; addressTo: Address } => {
  const addressFrom = batchEntry?.addressFrom || order.addressFrom;
  const addressTo = batchEntry?.addressTo || order.toAddress;

  return {
    addressFrom: addressFrom!,
    addressTo: addressTo!,
  };
};

// This is a typesafe version of the function above. Please use this and refactor existing code when possible.
export const getAddressesTypeSafe = (
  order?: Order,
  batchEntry?: BatchEntryExtended | null,
): { addressFrom: Address | undefined; addressTo: Address | undefined } => {
  const addressFrom = batchEntry?.addressFrom || order?.addressFrom;
  const addressTo = batchEntry?.addressTo || order?.toAddress;

  return {
    addressFrom: addressFrom || undefined,
    addressTo: addressTo || undefined,
  };
};

// USPS
export const isUntrackableInternationalDestination = (address: BaseAddress): boolean =>
  !!address.country &&
  !uspsTrackingCountryIso2Whitelist.includes(address.country.iso2) &&
  !uspsTrackingCountryAdditionalWhitelist.includes(address.country.iso2 as string);

export const isAddressCrossBorder = (senderAddress?: Address, recipientAddress?: Address) => {
  if (!senderAddress || !recipientAddress) return false;

  return (
    !!senderAddress.country &&
    !!recipientAddress.country &&
    senderAddress.country.iso2 !== recipientAddress.country.iso2
  );
};

export const isAddressEmpty = (address: BaseAddress | null) => {
  if (!address) return true;

  const IMPORTED_ORDER_EMPTY_ZIP = '00000';
  const isZipEmpty = !address.zip || address.zip === IMPORTED_ORDER_EMPTY_ZIP;

  const isMissingAllFields = !address.city && !address.state && !address.street1;

  if (isZipEmpty && isMissingAllFields) return true;

  return false;
};

export default Address;
